import { safeErr, openStatus } from '../App';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { getEmails, getEmailCounts, composeEmail, fetchInbox,
         markEmailRead, toggleEmailStar, deleteEmail, replyToEmail,
         aiDraft, getContacts, uploadAttachment, deleteAttachment, downloadAttachment,
         getSettings, correctText } from '../api';

/* ─── Attachment picker + chips (shared by Compose and Reply) ─────────────── */
function AttachmentBar({ attachments, onAdd, onRemove, uploading }) {
  const inputRef = useRef(null);
  return (
    <div style={{display:'flex',flexWrap:'wrap',gap:6,alignItems:'center',padding:'8px 16px',
      borderTop:'1px solid var(--border)',background:'var(--surface2)'}}>
      <input ref={inputRef} type="file" multiple style={{display:'none'}}
        onChange={e=>{ const files=[...e.target.files]; if(files.length) onAdd(files); e.target.value=''; }}/>
      <button type="button" className="btn btn-sm" disabled={uploading}
        onClick={()=>inputRef.current?.click()}>
        {uploading ? <><span className="spinner spinner-sm"/>Attaching…</> : '📎 Attach'}
      </button>
      {attachments.map(a=>(
        <span key={a.id} style={{display:'flex',alignItems:'center',gap:5,fontSize:12,
          padding:'4px 9px',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:6}}>
          📄 {a.filename}
          <span style={{color:'var(--text-3)'}}>({Math.round(a.size/1024)}KB)</span>
          <button type="button" onClick={()=>onRemove(a.id)}
            style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-3)',
              fontSize:12,lineHeight:1,padding:0}}>✕</button>
        </span>
      ))}
    </div>
  );
}

/* ─── HTML Body Renderer (iframe) ─────────────────────────────────────────── */
function HtmlBody({ email }) {
  const ref = useRef(null);
  const src = email.body_html || email.body || '';
  const isHtml = email.content_type === 'html' || !!email.body_html ||
    /<!doctype|<html|<head|<body|<div|<table|<span|<p[\s>]/i.test(src);

  useEffect(() => {
    if (!isHtml || !ref.current) return;
    const iframe = ref.current;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;
    const safe = src.replace(/<script[\s\S]*?<\/script>/gi,'')
                    .replace(/\son\w+\s*=\s*"[^"]*"/gi,'')
                    .replace(/\son\w+\s*=\s*'[^']*'/gi,'');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<style>* {box-sizing:border-box}
body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Arial,sans-serif;
  font-size:14px;line-height:1.6;color:#111827;margin:0;padding:16px;word-wrap:break-word;}
img{max-width:100%!important;height:auto!important}
a{color:#2563EB} table{max-width:100%!important} td{word-break:break-word}
pre,code{white-space:pre-wrap;word-break:break-all}
blockquote{border-left:3px solid #d1d5db;margin:8px 0;padding:4px 12px;color:#6b7280}
</style></head><body>${safe}</body></html>`;
    doc.open('text/html','replace'); doc.write(html); doc.close();
    const resize = () => {
      try {
        const h = doc.documentElement.scrollHeight || doc.body.scrollHeight;
        if (h > 50) iframe.style.height = (h + 32) + 'px';
      } catch(e) {}
    };
    [120,400,900,2000].forEach(ms => setTimeout(resize, ms));
  }, [email.id, src]);

  if (isHtml) return (
    <div style={{flex:1,overflowY:'auto',background:'#fff'}}>
      <iframe ref={ref} title="email" sandbox="allow-same-origin" scrolling="no"
        style={{width:'100%',minHeight:200,height:500,border:'none',display:'block',background:'#fff'}}/>
    </div>
  );
  return (
    <div style={{flex:1,overflow:'auto',padding:'18px 20px'}}>
      <pre style={{fontSize:14,lineHeight:1.8,color:'var(--text)',whiteSpace:'pre-wrap',
        fontFamily:'inherit',margin:0,wordBreak:'break-word'}}>{email.body||'(No content)'}</pre>
    </div>
  );
}

/* ─── Formatting Toolbar ───────────────────────────────────────────────────── */
function FormatToolbar({ onFormat }) {
  const tools = [
    { icon:'B',  cmd:'bold',        title:'Bold',         style:{fontWeight:700} },
    { icon:'I',  cmd:'italic',      title:'Italic',       style:{fontStyle:'italic'} },
    { icon:'U',  cmd:'underline',   title:'Underline',    style:{textDecoration:'underline'} },
    { icon:'≡',  cmd:'ul',          title:'Bullet list',  style:{} },
    { icon:'1.',  cmd:'ol',         title:'Numbered list',style:{} },
    { icon:'—',  cmd:'hr',          title:'Divider',      style:{} },
    { icon:'"',  cmd:'quote',       title:'Blockquote',   style:{} },
  ];
  return (
    <div style={{display:'flex',gap:2,padding:'6px 12px',borderBottom:'1px solid var(--border)',
      background:'var(--surface2)',flexWrap:'wrap',alignItems:'center'}}>
      {tools.map(t => (
        <button key={t.cmd}
          onMouseDown={e => { e.preventDefault(); onFormat(t.cmd); }}
          title={t.title}
          style={{...t.style, padding:'3px 8px', fontSize:13, border:'1px solid var(--border)',
            borderRadius:4, background:'var(--surface)', cursor:'pointer', minWidth:28,
            color:'var(--text)', lineHeight:1.4, fontFamily:'inherit'}}>
          {t.icon}
        </button>
      ))}
      <div style={{width:1,height:18,background:'var(--border)',margin:'0 4px'}}/>
      <select style={{fontSize:12,border:'1px solid var(--border)',borderRadius:4,
        padding:'2px 6px',background:'var(--surface)',color:'var(--text)',cursor:'pointer'}}
        onChange={e => onFormat('size:'+e.target.value)} defaultValue="">
        <option value="" disabled>Size</option>
        {['Small','Normal','Large','H1','H2'].map(s=><option key={s} value={s}>{s}</option>)}
      </select>
    </div>
  );
}

/* ─── Rich Compose Textarea with formatting ───────────────────────────────── */
function RichTextarea({ value, onChange, placeholder }) {
  const ref = useRef(null);

  // Auto-resize: expand to fit content so no internal scrollbar is needed
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, [value]);

  const applyFormat = (cmd) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const sel   = value.slice(start, end);
    const before = value.slice(0, start);
    const after  = value.slice(end);
    let newVal = value, cursor = end;

    if (cmd === 'bold')      { newVal = before + `**${sel}**` + after; cursor = end + 4; }
    else if (cmd === 'italic')    { newVal = before + `_${sel}_` + after; cursor = end + 2; }
    else if (cmd === 'underline') { newVal = before + `<u>${sel}</u>` + after; cursor = end + 7; }
    else if (cmd === 'ul') {
      const lines = (sel || 'Item').split('\n').map(l => `• ${l}`).join('\n');
      newVal = before + lines + after; cursor = start + lines.length;
    }
    else if (cmd === 'ol') {
      const lines = (sel || 'Item').split('\n').map((l,i) => `${i+1}. ${l}`).join('\n');
      newVal = before + lines + after; cursor = start + lines.length;
    }
    else if (cmd === 'hr')    { newVal = before + '\n---\n' + after; cursor = start + 5; }
    else if (cmd === 'quote') { newVal = before + `> ${sel || 'quote'}` + after; cursor = start + 2; }
    else if (cmd.startsWith('size:')) {
      const sz = cmd.split(':')[1];
      const wrap = {Small:'<small>',Normal:'',Large:'<large>',H1:'# ',H2:'## '};
      const wrapE= {Small:'</small>',Normal:'',Large:'</large>',H1:'',H2:''};
      newVal = before + (wrap[sz]||'') + sel + (wrapE[sz]||'') + after;
    }

    onChange(newVal);
    setTimeout(() => { el.focus(); el.setSelectionRange(cursor, cursor); }, 0);
  };

  return (
    <div style={{display:'flex',flexDirection:'column'}}>
      <FormatToolbar onFormat={applyFormat} />
      <textarea
        ref={ref}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || 'Write your message here…'}
        style={{width:'100%', minHeight:120, border:'none', outline:'none', resize:'none',
          fontSize:13.5, lineHeight:1.75, color:'var(--text)', background:'transparent',
          padding:'12px 16px', fontFamily:'inherit', overflow:'hidden', boxSizing:'border-box'}}
      />
    </div>
  );
}

/* ─── Compose Modal (full-size) ────────────────────────────────────────────── */
function ComposeModal({ onClose, onSent, initTo='', initName='', initCid=null,
                        replyTo=null, showToast }) {
  const [to,      setTo]      = useState(replyTo ? replyTo.from_email : initTo);
  const [toName,  setToName]  = useState(replyTo ? replyTo.from_name  : initName);
  const [subject, setSubject] = useState(replyTo ? 'Re: '+replyTo.subject : '');
  const [body,    setBody]    = useState('');
  const [sending, setSending] = useState(false);
  const [drafting,setDrafting]= useState(false);
  const [err,     setErr]     = useState('');
  const [contacts,setConts]   = useState([]);
  const [sugg,    setSugg]    = useState([]);
  const [showSugg,setShowSugg]= useState(false);
  const [attachments,setAttachments] = useState([]);
  const [uploading,  setUploading]   = useState(false);
  const [correcting, setCorrecting]  = useState(false);

  const handleCorrect = async () => {
    if (!body.trim()) return;
    setCorrecting(true);
    try {
      const r = await correctText(body);
      setBody(r.data.corrected || body);
    } catch(e) { showToast && showToast('Correction failed','error'); }
    finally { setCorrecting(false); }
  };

  useEffect(() => { getContacts().then(r=>setConts(r.data)); }, []);

  const handleAddFiles = async (files) => {
    setUploading(true);
    for (const f of files) {
      try { const r = await uploadAttachment(f); setAttachments(p=>[...p,r.data]); }
      catch(e) { showToast(e.response?.data?.detail||`Failed to attach ${f.name}`,'error'); }
    }
    setUploading(false);
  };
  const handleRemoveFile = async (id) => {
    try { await deleteAttachment(id); setAttachments(p=>p.filter(a=>a.id!==id)); }
    catch(e) { showToast('Failed to remove attachment','error'); }
  };

  const onToChange = v => {
    setTo(v); setShowSugg(true);
    setSugg(v.length > 0
      ? contacts.filter(c => c.email.toLowerCase().includes(v.toLowerCase()) ||
          c.name.toLowerCase().includes(v.toLowerCase())).slice(0,6)
      : []);
  };

  const pick = c => { setTo(c.email); setToName(c.name); setShowSugg(false); setSugg([]); };

  const handleAIDraft = async () => {
    if (!to && !toName) { showToast('Enter a recipient first','error'); return; }
    setDrafting(true);
    try {
      const cid     = initCid || contacts.find(c=>c.email===to)?.id || null;
      const contact = contacts.find(c=>c.email===to);
      const r = await aiDraft({ contact_id:cid, name:toName||to,
        company:contact?.company||'', sector:contact?.sector||'',
        role:contact?.role||'', purpose:'introduce our services and request a meeting' });
      setBody(r.data.draft||'');
      if (!subject && r.data.subject) setSubject(r.data.subject);
      else if (!subject) setSubject('Quick question for '+(toName||to));
    } catch(e) { showToast(e.response?.data?.detail||'AI draft failed','error'); }
    finally { setDrafting(false); }
  };

  const send = async () => {
    if (!to||!subject||!body) { setErr('To, subject and body are required'); return; }
    setSending(true); setErr('');
    try {
      const cid = initCid || contacts.find(c=>c.email===to)?.id || null;
      const attachment_ids = attachments.map(a=>a.id);
      if (replyTo) await replyToEmail(replyTo.id, {to_email:to,to_name:toName||to,subject,body,contact_id:cid,attachment_ids});
      else         await composeEmail({to_email:to,to_name:toName||to,subject,body,contact_id:cid,attachment_ids});
      onSent && onSent();
    } catch(e) { setErr(e.response?.data?.detail||'Failed to send'); }
    finally { setSending(false); }
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',
      display:'flex',alignItems:'center',justifyContent:'center',zIndex:200,padding:16}}>
      <div style={{background:'var(--surface)',borderRadius:14,
        width:'min(700px,96vw)', height:'min(620px,90vh)',
        boxShadow:'0 20px 60px rgba(0,0,0,.25)',display:'flex',flexDirection:'column',overflow:'hidden'}}>

        {/* Header */}
        <div style={{padding:'13px 18px',borderBottom:'1px solid var(--border)',
          display:'flex',alignItems:'center',justifyContent:'space-between',
          background:'var(--surface2)',flexShrink:0}}>
          <span style={{fontWeight:700,fontSize:15}}>
            {replyTo ? `↩ Reply: ${replyTo.subject}` : '✎ New message'}
          </span>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',
            fontSize:18,color:'var(--text-2)',lineHeight:1}}>✕</button>
        </div>

        {/* To + Subject */}
        <div style={{flexShrink:0,borderBottom:'1px solid var(--border)'}}>
          {err && <div style={{padding:'8px 16px',background:'var(--red-bg)',
            color:'var(--red)',fontSize:12,borderBottom:'1px solid var(--red-border)'}}>{err}</div>}

          {/* To */}
          <div style={{display:'flex',alignItems:'center',padding:'10px 16px',
            borderBottom:'1px solid var(--border)',position:'relative',gap:8}}>
            <span style={{fontSize:12,fontWeight:600,color:'var(--text-2)',width:54,flexShrink:0}}>To</span>
            <input value={to} onChange={e=>onToChange(e.target.value)}
              onFocus={()=>to.length>0&&setShowSugg(true)}
              onBlur={()=>setTimeout(()=>setShowSugg(false),180)}
              style={{flex:1,border:'none',outline:'none',fontSize:14,background:'transparent',color:'var(--text)'}}
              placeholder="recipient@email.com"/>
            {showSugg && sugg.length>0 && (
              <div style={{position:'absolute',top:'100%',left:70,right:0,background:'var(--surface)',
                border:'1px solid var(--border)',borderRadius:8,zIndex:10,
                boxShadow:'0 8px 24px rgba(0,0,0,.12)',maxHeight:220,overflow:'auto'}}>
                {sugg.map(c=>(
                  <div key={c.id} onMouseDown={()=>pick(c)}
                    style={{display:'flex',alignItems:'center',gap:10,padding:'9px 14px',
                      cursor:'pointer',fontSize:13}}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--blue-bg)'}
                    onMouseLeave={e=>e.currentTarget.style.background=''}>
                    <div style={{width:28,height:28,borderRadius:'50%',background:'var(--blue-bg)',
                      color:'var(--blue)',display:'flex',alignItems:'center',justifyContent:'center',
                      fontSize:11,fontWeight:600,flexShrink:0}}>
                      {c.name.slice(0,2).toUpperCase()}
                    </div>
                    <div>
                      <div style={{fontWeight:600}}>{c.name}</div>
                      <div style={{fontSize:11,color:'var(--text-2)'}}>{c.email} · {c.company}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Subject */}
          <div style={{display:'flex',alignItems:'center',padding:'10px 16px',gap:8}}>
            <span style={{fontSize:12,fontWeight:600,color:'var(--text-2)',width:54,flexShrink:0}}>Subject</span>
            <input value={subject} onChange={e=>setSubject(e.target.value)}
              style={{flex:1,border:'none',outline:'none',fontSize:14,background:'transparent',color:'var(--text)',fontWeight:500}}
              placeholder="Subject line"/>
          </div>
        </div>

        {/* Original quote for reply */}
        {replyTo && (
          <div style={{padding:'10px 16px',borderBottom:'1px solid var(--border)',
            background:'var(--surface2)',flexShrink:0,maxHeight:90,overflow:'auto'}}>
            <div style={{fontSize:11,fontWeight:600,color:'var(--text-2)',marginBottom:4}}>
              Original — {replyTo.from_name}:
            </div>
            <div style={{fontSize:12,color:'var(--text-2)',lineHeight:1.6,
              borderLeft:'3px solid var(--border-dark)',paddingLeft:10}}>
              {(replyTo.body||'').replace(/<[^>]+>/g,' ').trim().slice(0,250)}
              {replyTo.body?.length>250?'…':''}
            </div>
          </div>
        )}

        {/* Rich body area — takes all remaining space */}
        <div style={{flex:1,display:'flex',flexDirection:'column',minHeight:0,overflow:'hidden'}}>
          <RichTextarea value={body} onChange={setBody}
            placeholder="Write your message here… (use toolbar to format)"/>
        </div>

        <AttachmentBar attachments={attachments} uploading={uploading}
          onAdd={handleAddFiles} onRemove={handleRemoveFile}/>

        {/* Footer */}
        <div style={{padding:'12px 16px',borderTop:'1px solid var(--border)',
          display:'flex',justifyContent:'space-between',alignItems:'center',
          background:'var(--surface2)',flexShrink:0}}>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <button className="btn btn-primary" disabled={sending} onClick={send}
              style={{gap:6}}>
              {sending ? <><span className="spinner spinner-sm"/>Sending…</> : '✈ Send'}
            </button>
            <button className="btn" disabled={drafting} onClick={handleAIDraft}>
              {drafting ? <><span className="spinner spinner-sm"/>Drafting…</> : '✦ Write with AI'}
            </button>
            <button className="btn" disabled={correcting||!body.trim()} onClick={handleCorrect}>
              {correcting ? <><span className="spinner spinner-sm"/>Correcting…</> : '✎ Correct'}
            </button>
          </div>
          <button onClick={onClose}
            style={{background:'none',border:'none',cursor:'pointer',fontSize:13,
              color:'var(--text-2)',padding:'4px 8px'}}>Discard</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Email Row ────────────────────────────────────────────────────────────── */
const plainSnippet = em => (em.body||'').replace(/<[^>]+>/g,' ').replace(/\s+/g,' ').trim().slice(0,80);

function EmailRow({ email, active, folder, onSelect, onStar, onDelete }) {
  const isUnread = !email.read && folder==='inbox';
  const sender   = folder==='inbox'
    ? (email.from_name||email.from_email||'').split('<')[0].trim()
    : (email.to_name  ||email.to_email  ||'').split('<')[0].trim();
  return (
    <div onClick={onSelect} style={{
      display:'flex',alignItems:'center',gap:7,padding:'10px 12px',
      borderBottom:'1px solid var(--border)',cursor:'pointer',
      background: active?'var(--blue-bg)':isUnread?'#FAFBFF':'',
      transition:'background .1s',minWidth:0,overflow:'hidden'}}>
      <button onMouseDown={e=>{e.stopPropagation();e.preventDefault();onStar();}}
        style={{background:'none',border:'none',cursor:'pointer',fontSize:14,padding:'0 2px',
          flexShrink:0,lineHeight:1,color:email.starred?'#D97706':'var(--text-3)'}}>
        {email.starred?'★':'☆'}
      </button>
      <div style={{width:7,height:7,borderRadius:'50%',flexShrink:0,
        background:isUnread?'var(--blue)':'transparent'}}/>
      <div style={{width:110,flexShrink:0,overflow:'hidden',textOverflow:'ellipsis',
        whiteSpace:'nowrap',fontSize:12.5,fontWeight:isUnread?700:500,color:'var(--text)'}}>
        {sender||'(unknown)'}
      </div>
      <div style={{flex:1,minWidth:0,overflow:'hidden',display:'flex',
        alignItems:'baseline',gap:5}}>
        <span style={{fontSize:12.5,fontWeight:isUnread?600:400,color:'var(--text)',
          flexShrink:0,maxWidth:'52%',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
          {email.subject||'(no subject)'}
        </span>
        <span style={{fontSize:11.5,color:'var(--text-3)',overflow:'hidden',
          textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1,minWidth:0}}>
          {plainSnippet(email).slice(0,55)}
        </span>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:5,flexShrink:0}}>
        {folder==='sent' && openStatus(email.opened_at, email.open_count).opened &&
          <span style={{fontSize:12}} title={openStatus(email.opened_at, email.open_count).tooltip}>👁</span>}
        {email.attachments?.length>0 &&
          <span style={{fontSize:12,color:'var(--text-3)'}} title={`${email.attachments.length} attachment(s)`}>📎</span>}
        <span style={{fontSize:11,color:'var(--text-2)',whiteSpace:'nowrap'}}>
          {new Date(email.sent_at).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}
        </span>
        <button onMouseDown={e=>{e.stopPropagation();e.preventDefault();onDelete();}}
          style={{background:'none',border:'none',cursor:'pointer',fontSize:12,
            color:'var(--text-3)',padding:'0 2px',lineHeight:1,opacity:0}}
          className="del-btn">✕</button>
      </div>
    </div>
  );
}


/* ─── Gmail-style Header Details ──────────────────────────────────────────── */
function HeaderDetails({ em, isMe, myEmail }) {
  const [showAll, setShowAll] = React.useState(false);

  // For inbox emails: from = the sender (lead/external), to = you
  // For sent emails:  from = you, to = the recipient (lead)
  const fromFull = em.from_name && em.from_name !== em.from_email
    ? `${em.from_name} <${em.from_email}>`
    : em.from_email || '—';
  const toFull   = em.to_name && em.to_name !== em.to_email
    ? `${em.to_name} <${em.to_email}>`
    : em.to_email || '—';
  const dateStr   = new Date(em.sent_at).toLocaleString('en-IN', {
    weekday:'short', day:'2-digit', month:'short', year:'numeric',
    hour:'2-digit', minute:'2-digit', hour12:true,
  });

  // Extract domain for "mailed-by"
  const mailedBy  = (em.from_email||'').split('@')[1] || '';

  const Row = ({ label, value, mono }) => (
    <div style={{display:'flex',gap:10,padding:'2px 0',fontSize:12,lineHeight:1.6}}>
      <span style={{color:'var(--text-2)',minWidth:70,flexShrink:0,textAlign:'right'}}>{label}</span>
      <span style={{color:'var(--text)',fontFamily:mono?'monospace':'inherit',
        wordBreak:'break-all'}}>{value}</span>
    </div>
  );

  return (
    <div style={{marginTop:4}} onClick={e=>e.stopPropagation()}>
      {/* Compact single-line — click to expand */}
      {!showAll ? (
        <div style={{display:'flex',alignItems:'center',gap:5,fontSize:11}}>
          <span style={{color:'var(--text-2)'}}>
            {isMe ? `to ${toFull}` : `from ${fromFull}`}
          </span>
          <button onClick={()=>setShowAll(true)}
            style={{background:'none',border:'none',cursor:'pointer',
              color:'var(--blue)',fontSize:11,padding:0,lineHeight:1,
              display:'flex',alignItems:'center',gap:2,flexShrink:0}}>
            ▾ details
          </button>
        </div>
      ) : (
        /* Full Gmail-style details panel */
        <div style={{
          marginTop:6,
          background:'var(--surface2)',
          border:'1px solid var(--border)',
          borderRadius:8,
          padding:'10px 14px',
          fontSize:12,
        }}>
          <Row label="from:"    value={fromFull}/>
          <Row label="to:"      value={toFull}/>
          {em.subject &&
            <Row label="subject:"  value={em.subject}/>}
          <Row label="date:"    value={dateStr}/>
          {mailedBy &&
            <Row label="mailed-by:" value={mailedBy}/>}
          {mailedBy &&
            <Row label="signed-by:" value={mailedBy}/>}
          <Row label="security:"  value="Standard encryption (TLS)"/>
          {em.gmail_id &&
            <Row label="id:" value={em.gmail_id} mono/>}

          <button onClick={()=>setShowAll(false)}
            style={{marginTop:6,background:'none',border:'none',cursor:'pointer',
              color:'var(--blue)',fontSize:11,padding:0,lineHeight:1,
              display:'flex',alignItems:'center',gap:2}}>
            ▴ hide details
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Thread View (Gmail-style) ────────────────────────────────────────────── */
function ThreadView({ email, allEmails, onClose, onReply, onStar, onDelete, showToast, sendingEmail }) {
  const [expandedIds, setExpandedIds] = useState(new Set([email.id]));
  const [replyOpen,   setReplyOpen]   = useState(false);
  const [replyBody,   setReplyBody]   = useState('');
  const [sending,     setSending]     = useState(false);
  const [drafting,    setDrafting]    = useState(false);
  const [rAttachments,setRAttachments]= useState([]);
  const [rUploading,  setRUploading]  = useState(false);
  const [correcting,  setCorrecting]  = useState(false);

  const handleAddFiles = async (files) => {
    setRUploading(true);
    for (const f of files) {
      try { const r = await uploadAttachment(f); setRAttachments(p=>[...p,r.data]); }
      catch(e) { showToast && showToast(e.response?.data?.detail||`Failed to attach ${f.name}`,'error'); }
    }
    setRUploading(false);
  };
  const handleRemoveFile = async (id) => {
    try { await deleteAttachment(id); setRAttachments(p=>p.filter(a=>a.id!==id)); }
    catch(e) { showToast && showToast('Failed to remove attachment','error'); }
  };

  // Find all emails in the same thread
  const threadEmails = email.thread_id
    ? allEmails.filter(e => e.thread_id === email.thread_id || e.id === email.id)
        .sort((a,b) => new Date(a.sent_at) - new Date(b.sent_at))
    : [email];
  const unique = [...new Map(threadEmails.map(e=>[e.id,e])).values()];

  const toggleExpand = id => setExpandedIds(p => {
    const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const handleAIDraft = async () => {
    setDrafting(true);
    try {
      // Use the latest inbound message in the thread as context for the reply
      const lastClientMsg = [...unique].reverse().find(e => e.from_email?.toLowerCase() !== myEmail);
      const ctx = lastClientMsg || email;
      const r = await aiDraft({
        email_id:         ctx.id,
        name:             email.from_name || email.from_email,
        reply_to_message: ctx.body || '',
      });
      setReplyBody(r.data.draft||'');
    } catch(e) { showToast && showToast(e.response?.data?.detail||'AI draft failed','error'); }
    finally { setDrafting(false); }
  };

  const handleSend = async () => {
    if (!replyBody.trim()) return;
    setSending(true);
    try {
      await replyToEmail(email.id, {
        to_email: email.from_email,
        to_name:  email.from_name||email.from_email,
        subject:  'Re: ' + (email.subject||'').replace(/^(Re:\s*)+/i,''),
        body:     replyBody,
        contact_id: email.contact_id,
        attachment_ids: rAttachments.map(a=>a.id),
      });
      setReplyOpen(false); setReplyBody(''); setRAttachments([]);
      onReply && onReply();
    } catch(e) {}
    finally { setSending(false); }
  };

  const handleCorrect = async () => {
    if (!replyBody.trim()) return;
    setCorrecting(true);
    try {
      const r = await correctText(replyBody);
      setReplyBody(r.data.corrected || replyBody);
    } catch(e) { showToast && showToast('Correction failed','error'); }
    finally { setCorrecting(false); }
  };

  const myEmail = sendingEmail || '';

  return (
    <div style={{flex:1,display:'flex',flexDirection:'column',minWidth:0,overflow:'hidden'}}>

      {/* ── Top header ── */}
      <div style={{padding:'12px 18px',borderBottom:'1px solid var(--border)',
        display:'flex',alignItems:'center',gap:10,flexShrink:0,background:'var(--surface)'}}>
        <div style={{flex:1,fontWeight:700,fontSize:15,overflow:'hidden',
          textOverflow:'ellipsis',whiteSpace:'nowrap',minWidth:0}}>
          {email.subject||'(no subject)'}
          {unique.length > 1 &&
            <span style={{fontSize:12,fontWeight:400,color:'var(--text-2)',marginLeft:8}}>
              ({unique.length} messages)
            </span>}
        </div>
        <div style={{display:'flex',gap:6,flexShrink:0}}>
          <button className="btn btn-primary btn-sm"
            onClick={()=>{setReplyOpen(p=>!p);setReplyBody('');}}>
            {replyOpen ? '✕ Close' : '↩ Reply'}
          </button>
          <button onClick={onStar} style={{background:'none',border:'1px solid var(--border)',
            borderRadius:6,padding:'4px 8px',cursor:'pointer',fontSize:15,lineHeight:1,
            color:email.starred?'#D97706':'var(--text-2)'}}>
            {email.starred?'★':'☆'}
          </button>
          <button className="btn btn-danger btn-sm" onClick={onDelete}>🗑</button>
          <button className="btn btn-sm" onClick={onClose}>✕</button>
        </div>
      </div>

      {/* ── Thread messages ── */}
      <div style={{flex:1,overflowY:'auto',padding:'12px 18px',
        display:'flex',flexDirection:'column',gap:8,minHeight:0}}>
        {unique.map((em, idx) => {
          const isMe     = em.from_email?.toLowerCase() === myEmail;
          const expanded = expandedIds.has(em.id);
          const isLast   = idx === unique.length - 1;
          const senderInitials = (isMe ? 'Me' : (em.from_name||em.from_email||'?')).slice(0,2).toUpperCase();
          const senderLabel    = isMe ? 'Me' : (em.from_name || em.from_email);
          const formattedDate  = new Date(em.sent_at).toLocaleString('en-IN', {
            weekday:'short', day:'2-digit', month:'short', year:'numeric',
            hour:'2-digit', minute:'2-digit', hour12:true
          });
          const os = openStatus(em.opened_at, em.open_count);

          return (
            <div key={em.id} style={{border:'1px solid var(--border)',borderRadius:10,
              overflow:'hidden',background:'var(--surface)',flexShrink:0,
              boxShadow:isLast?'0 2px 8px rgba(0,0,0,.06)':'none'}}>

              {/* ── Clickable message header ── */}
              <div onClick={()=>toggleExpand(em.id)}
                style={{padding:'10px 14px',display:'flex',alignItems:'flex-start',gap:10,
                  cursor:'pointer',
                  background:expanded?'var(--surface)':'var(--surface2)',
                  borderBottom:expanded?'1px solid var(--border)':'none',
                  transition:'background .1s'}}>

                {/* Avatar */}
                <div style={{width:34,height:34,borderRadius:'50%',flexShrink:0,
                  background:isMe?'var(--blue-bg)':'var(--green-bg)',
                  color:isMe?'var(--blue)':'var(--green)',
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:12,fontWeight:700,marginTop:1}}>
                  {senderInitials}
                </div>

                {/* Name + details */}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',alignItems:'baseline',gap:7,flexWrap:'wrap'}}>
                    <span style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>{senderLabel}</span>
                    {/* Collapsed: show snippet */}
                    {!expanded &&
                      <span style={{fontSize:12,color:'var(--text-3)',
                        overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1,minWidth:0}}>
                        {plainSnippet(em).slice(0,70)}
                      </span>}
                  </div>

                  {/* Expanded: Gmail-style header details — click to toggle */}
                  {expanded && (
                    <HeaderDetails em={em} isMe={isMe} myEmail={myEmail}/>
                  )}
                </div>

                {/* Date + chevron */}
                <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0,marginTop:1}}>
                  {isMe && (
                    <span className={`pill ${os.opened?'pill-green':'pill-gray'}`} style={{fontSize:10}}
                      title={os.tooltip}>
                      {os.label}
                    </span>
                  )}
                  <span style={{fontSize:11,color:'var(--text-2)',whiteSpace:'nowrap'}}>
                    {new Date(em.sent_at).toLocaleString('en-IN',
                      {day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit'})}
                  </span>
                  <span style={{fontSize:11,color:'var(--text-3)',display:'inline-block',
                    transform:expanded?'rotate(180deg)':'',transition:'transform .2s'}}>▾</span>
                </div>
              </div>

              {/* Body */}
              {expanded && <HtmlBody email={em}/>}
              {expanded && em.attachments?.length>0 && (
                <div style={{padding:'10px 14px',borderTop:'1px solid var(--border)',
                  display:'flex',flexWrap:'wrap',gap:8,background:'var(--surface)'}}>
                  {em.attachments.map(a=>(
                    <button key={a.id} onClick={()=>downloadAttachment(a.id,a.filename)}
                      style={{display:'flex',alignItems:'center',gap:6,fontSize:12,padding:'6px 10px',
                        background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:8,
                        cursor:'pointer',color:'var(--text)'}}>
                      📄 {a.filename}
                      <span style={{color:'var(--text-3)'}}>({Math.round(a.size/1024)}KB)</span> ⬇
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Reply composer: PINNED to bottom, always visible ── */}
      {replyOpen && (
        <div style={{flexShrink:0,borderTop:'2px solid var(--blue)',background:'var(--surface)',
          display:'flex',flexDirection:'column',maxHeight:'70vh',overflowY:'auto'}}>
          {/* Reply header bar */}
          <div style={{padding:'8px 14px',background:'var(--blue-bg)',
            borderBottom:'1px solid var(--blue-border)',
            display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0}}>
            <span style={{fontSize:13,fontWeight:600,color:'var(--blue)'}}>
              ↩ Replying to {email.from_name||email.from_email}
            </span>
            <div style={{display:'flex',gap:6}}>
              <button className="btn btn-sm" disabled={drafting} onClick={handleAIDraft}>
                {drafting?<><span className="spinner spinner-sm"/>Drafting…</>:'✦ Write with AI'}
              </button>
              <button className="btn btn-sm" disabled={correcting||!replyBody.trim()} onClick={handleCorrect}>
                {correcting?<><span className="spinner spinner-sm"/>Correcting…</>:'✎ Correct'}
              </button>
              <button className="btn btn-sm"
                onClick={()=>{setReplyOpen(false);setReplyBody('');}}>✕</button>
            </div>
          </div>
          {/* Formatting toolbar + textarea */}
          <div>
            <RichTextarea value={replyBody} onChange={setReplyBody}
              placeholder="Write your reply here…"/>
          </div>
          <AttachmentBar attachments={rAttachments} uploading={rUploading}
            onAdd={handleAddFiles} onRemove={handleRemoveFile}/>
          {/* Send bar */}
          <div style={{padding:'8px 14px',borderTop:'1px solid var(--border)',
            display:'flex',justifyContent:'space-between',alignItems:'center',
            background:'var(--surface2)',flexShrink:0}}>
            <span style={{fontSize:11,color:'var(--text-2)'}}>From {myEmail}</span>
            <button className="btn btn-primary btn-sm"
              disabled={sending||!replyBody.trim()} onClick={handleSend}>
              {sending?<><span className="spinner spinner-sm"/>Sending…</>:'✈ Send reply'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ─── Main EmailManager ─────────────────────────────────────────────────────── */
export default function EmailManager({ showToast, onCountChange }) {
  const loc    = useLocation();
  const nav    = useNavigate();
  const params = new URLSearchParams(loc.search);

  const [folder,       setFolder]       = useState(params.get('folder') || 'inbox');
  const [emails,       setEmails]       = useState([]);
  const [counts,       setCounts]       = useState({inbox:0,sent:0,unread:0});
  const [selected,     setSelected]     = useState(null);
  const [search,       setSearch]       = useState('');
  const [loading,      setLoading]      = useState(true);
  const [fetching,     setFetch]        = useState(false);
  const [compose,      setCompose]      = useState(false);
  const [cleaning,     setCleaning]     = useState(false);
  const [sendingEmail, setSendingEmail] = useState('');

  useEffect(() => {
    getSettings().then(r => setSendingEmail(r.data.sending_email || '')).catch(()=>{});
  }, []);

  const initTo   = params.get('to')   || '';
  const initName = decodeURIComponent(params.get('name') || '');
  const initCid  = params.get('cid')  ? parseInt(params.get('cid')) : null;
  const openId   = params.get('open') ? parseInt(params.get('open')): null;

  useEffect(() => { if (params.get('compose')==='1') setCompose(true); }, [loc.search]);

  const loadEmails = useCallback(() => {
    setLoading(true);
    Promise.all([getEmails({folder, search}), getEmailCounts()])
      .then(([eRes,cRes]) => {
        setEmails(eRes.data);
        setCounts(cRes.data);
        onCountChange && onCountChange(cRes.data.unread||0);
        if (openId) {
          const found = eRes.data.find(e=>e.id===openId);
          if (found) setSelected(found);
        }
      }).finally(()=>setLoading(false));
  }, [folder, search]);

  useEffect(()=>{ loadEmails(); }, [folder]);
  useEffect(()=>{ const t=setTimeout(loadEmails,350); return()=>clearTimeout(t); }, [search]);

  // WebSocket real-time
  useEffect(() => {
    const handler = e => {
      try {
        const d = JSON.parse(e.data);
        if (d.type==='new_replies' && (folder==='inbox'||folder==='all')) loadEmails();
      } catch(err) {}
    };
    window.__salesai_ws_handler = handler;
    return () => { window.__salesai_ws_handler = null; };
  }, [folder, loadEmails]);

  const handleSelect = async em => {
    setSelected(em);
    if (!em.read && em.folder==='inbox') {
      await markEmailRead(em.id);
      setEmails(prev=>prev.map(e=>e.id===em.id?{...e,read:true}:e));
      setCounts(p=>({...p,unread:Math.max(0,p.unread-1)}));
      onCountChange && onCountChange(Math.max(0,counts.unread-1));
    }
  };

  const handleStar = async em => {
    await toggleEmailStar(em.id);
    setEmails(prev=>prev.map(e=>e.id===em.id?{...e,starred:!e.starred}:e));
    if (selected?.id===em.id) setSelected(p=>({...p,starred:!p.starred}));
  };

  const handleDelete = async em => {
    if (!window.confirm('Delete this email?')) return;
    await deleteEmail(em.id);
    if (selected?.id===em.id) setSelected(null);
    loadEmails(); showToast('Email deleted');
  };

  const handleFetch = async () => {
    setFetch(true);
    try {
      const r = await fetchInbox();
      showToast(r.data.unread>0 ? `${r.data.unread} unread emails` : 'Inbox up to date');
      loadEmails();
    } catch(e) { showToast('Fetch failed','error'); }
    finally { setFetch(false); }
  };

  const handleCleanInbox = async () => {
    if (!window.confirm('This will remove all non-SalesAI emails from the inbox list (they remain in Gmail). Continue?')) return;
    setCleaning(true);
    try {
      const r = await api.post('/emails/clear-non-salesai');
      showToast(`✓ Cleaned inbox — removed ${r.data.deleted} non-SalesAI emails`);
      loadEmails();
    } catch(e) { showToast('Cleanup failed','error'); }
    finally { setCleaning(false); }
  };

  const FOLDERS = [
    { id:'inbox', label:'Inbox',   icon:'📥', badge: counts.unread||null },
    { id:'sent',  label:'Sent',    icon:'📤', badge: null },
    { id:'all',   label:'All mail',icon:'📋', badge: null },
  ];

  return (
    <>
      {(compose||initTo) && (
        <ComposeModal initTo={initTo} initName={initName} initCid={initCid}
          onClose={()=>{ setCompose(false); if(initTo) nav('/email-manager'); }}
          onSent={()=>{ setCompose(false); if(initTo) nav('/email-manager');
            showToast('Email sent!'); loadEmails(); }}
          showToast={showToast}/>
      )}

      <div className="email-layout">
        {/* Folder sidebar */}
        <div className="email-sidebar">
          <div style={{padding:'10px 8px'}}>
            <button className="btn btn-primary" style={{width:'100%',justifyContent:'center',fontSize:13}}
              onClick={()=>setCompose(true)}>✎ Compose</button>
          </div>

          {FOLDERS.map(f=>(
            <button key={f.id} className={`email-folder ${folder===f.id?'active':''}`}
              onClick={()=>{ setFolder(f.id); setSelected(null); }}>
              <span style={{fontSize:14}}>{f.icon}</span>
              <span style={{flex:1}}>{f.label}</span>
              {f.badge>0 && <span className="nav-badge">{f.badge}</span>}
            </button>
          ))}

          <div style={{height:1,background:'var(--border)',margin:'6px 8px'}}/>

          <button className="email-folder" disabled={fetching} onClick={handleFetch}>
            <span style={{fontSize:14}}>⟳</span>
            {fetching?'Fetching…':'Refresh inbox'}
          </button>

          <button className="email-folder" disabled={cleaning} onClick={handleCleanInbox}
            style={{color:'var(--amber)'}}>
            <span style={{fontSize:14}}>🧹</span>
            {cleaning?'Cleaning…':'Clean inbox'}
          </button>

          <div style={{padding:'8px 10px 4px',fontSize:9,fontWeight:600,
            color:'var(--text-3)',textTransform:'uppercase',letterSpacing:'.06em',marginTop:4}}>
            Info
          </div>
          <div style={{padding:'0 12px 10px',fontSize:11,color:'var(--text-3)',lineHeight:1.5}}>
            Only shows replies to SalesAI emails. Use "Clean inbox" to remove old emails.
          </div>
        </div>

        {/* Email list */}
        <div className={`email-list ${selected?'narrow':''}`}>
          <div style={{padding:'9px 12px',borderBottom:'1px solid var(--border)',flexShrink:0}}>
            <input className="form-input" style={{fontSize:12,width:'100%'}}
              placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <div style={{overflow:'auto',flex:1}}>
            {loading ? (
              <div style={{display:'flex',alignItems:'center',justifyContent:'center',padding:40}}>
                <div className="spinner spinner-md"/>
              </div>
            ) : emails.length===0 ? (
              <div style={{textAlign:'center',padding:'40px 16px',color:'var(--text-2)'}}>
                <div style={{fontSize:32,opacity:.3,marginBottom:10}}>
                  {folder==='inbox'?'📥':'📤'}
                </div>
                <div style={{fontSize:13,marginBottom:12}}>
                  {folder==='inbox'
                    ? 'No emails yet — send emails to leads first, then click Refresh'
                    : 'No sent emails yet'}
                </div>
                {folder==='inbox' &&
                  <button className="btn btn-sm" onClick={handleFetch}>⟳ Refresh now</button>}
              </div>
            ) : emails.map(em=>(
              <EmailRow key={em.id} email={em} folder={folder}
                active={selected?.id===em.id}
                onSelect={()=>handleSelect(em)}
                onStar={()=>handleStar(em)}
                onDelete={()=>handleDelete(em)}/>
            ))}
          </div>
        </div>

        {/* Thread / email view */}
        {selected ? (
          <ThreadView
            email={selected}
            allEmails={emails}
            onClose={()=>setSelected(null)}
            onReply={()=>{ loadEmails(); showToast('Reply sent!'); }}
            onStar={()=>handleStar(selected)}
            onDelete={()=>handleDelete(selected)}
            showToast={showToast}
            sendingEmail={sendingEmail}/>
        ) : (
          <div style={{flex:1,display:'flex',flexDirection:'column',
            alignItems:'center',justifyContent:'center',color:'var(--text-3)',gap:12}}>
            <div style={{fontSize:48,opacity:.2}}>✉</div>
            <div style={{fontSize:13}}>Select an email to read</div>
            <button className="btn btn-primary btn-sm" onClick={()=>setCompose(true)}>
              ✎ Compose new
            </button>
          </div>
        )}
      </div>
    </>
  );
}
